import { CropType, cropTypes, Regions } from '@agroone/entities'

/**
 * CEPP business rules (Certificats d'Économie de Produits Phytopharmaceutiques).
 *
 * French regulatory scheme: recommending certain pesticides or alternative solutions
 * credits a standardized CEPP action, identified by a reference like `2017-023`.
 * These references are displayed on the recommendation PDF, only for the
 * France South West region.
 *
 * A pesticide is identified by `${identificationNumber}#${name}` (AMM number + product name).
 */

type PesticideCeppRefs = Readonly<Record<string, string>>

const SWEET_CORN_PESTICIDE_REFS: PesticideCeppRefs = {
  '2190173#METAREX DUO': '2017-023',
  '2190173#HELEXIOM DUO': '2017-023',
  '2190173#ALLOWIN DUO': '2017-023',
  '2100030#SLUXX HP': '2017-023',
  '2160226#IRONMAX PRO': '2017-023',
  '9200482#DELFIN': '2018-034',
  '2010513#DIPEL DF': '2018-034',
  '2020241#XENTARI': '2018-034',
}

const PEAS_AND_BEANS_PESTICIDE_REFS: PesticideCeppRefs = {
  '2100030#SLUXX HP': '2017-023',
  '2010513#DIPEL DF': '2018-034',
  '9900189#CONTANS WG': '2017-026',
}

const PULSES_PESTICIDE_REFS: PesticideCeppRefs = {
  '2100030#SLUXX HP': '2017-023',
  '2010513#DIPEL DF': '2018-034',
}

const PESTICIDE_REFS_BY_CROP_TYPE: Partial<Record<CropType, PesticideCeppRefs>> = {
  [cropTypes.SWEET_CORN]: SWEET_CORN_PESTICIDE_REFS,
  [cropTypes.PEAS]: PEAS_AND_BEANS_PESTICIDE_REFS,
  [cropTypes.BEANS]: PEAS_AND_BEANS_PESTICIDE_REFS,
  [cropTypes.SHELLED_BEANS]: PULSES_PESTICIDE_REFS,
  [cropTypes.LENTIL]: PULSES_PESTICIDE_REFS,
  [cropTypes.CHICK_PEA]: PULSES_PESTICIDE_REFS,
}

/** CEPP action credited when mechanical hoeing is recommended as the alternative solution. */
const HOEING_REF_BY_CROP_TYPE: Partial<Record<CropType, string>> = {
  [cropTypes.SWEET_CORN]: '2021-030',
  [cropTypes.PEAS]: '2021-086',
  [cropTypes.BEANS]: '2021-086',
  [cropTypes.SHELLED_BEANS]: '2021-030',
  [cropTypes.LENTIL]: '2021-030',
  [cropTypes.CHICK_PEA]: '2021-030',
}

const HOEING_ALTERNATIVE = 'hoeing'

export const CeppRules = {
  /** CEPP references only appear on recommendations for the France South West region. */
  appliesToRegion: (regionName: string | undefined): boolean => regionName === Regions.FRANCE_SOUTH_WEST,

  /**
   * Returns the CEPP action references credited by recommending the given pesticide and/or
   * the hoeing alternative solution for the given crop type, in display order (pesticide
   * reference first). Empty when the crop type has no CEPP-eligible actions.
   */
  getRefs: (
    cropType: string,
    pesticide: { identificationNumber?: string | number; name?: string },
    alternativeSolution?: string,
  ): string[] => {
    const refs: string[] = []

    const pesticideRefs = PESTICIDE_REFS_BY_CROP_TYPE[cropType as CropType]
    if (pesticideRefs && pesticide.identificationNumber && pesticide.name) {
      const ref = pesticideRefs[`${pesticide.identificationNumber}#${pesticide.name}`]
      if (ref) {
        refs.push(ref)
      }
    }

    if (alternativeSolution === HOEING_ALTERNATIVE) {
      const hoeingRef = HOEING_REF_BY_CROP_TYPE[cropType as CropType]
      if (hoeingRef) {
        refs.push(hoeingRef)
      }
    }

    return refs
  },
} as const
